import Cookies from "js-cookie";
import { pick, snakeCase } from "lodash";
import { camelCase } from "lodash/fp";
import * as yup from "yup";

import { LocalRegionType, PatientDto, UserDto } from "graphql/generated";
import { CURRENT_COUNTRY } from "src/constants/Countries";

function cleanObject(obj) {
  Object.keys(obj).forEach((key) => {
    // Remove key if the value is null or undefined
    if (obj[key] === null || obj[key] === undefined) {
      delete obj[key];
    } else if (typeof obj[key] === "object" && !Array.isArray(obj[key])) {
      // If the value is an object, call cleanObject recursively
      obj[key] = cleanObject(obj[key]);

      // If the recursive call returns an empty object, delete the key
      if (Object.keys(obj[key]).length === 0) {
        delete obj[key];
      }
    }
    // Note: This does not handle arrays or Date objects or other complex objects properly
  });
  return obj;
}

const traitSchema = yup.object().shape({
  email: yup.string().email().required(),
  createdAt: yup.date().required(),
  firstName: yup.string(),
  lastName: yup.string(),
  name: yup.string(),
  phone: yup.string(),
  address: yup.object().shape({}),
  birthday: yup.date(),
  id: yup.string().required()
});

export const buildTraits = (
  user: Pick<UserDto, "email" | "id">,
  patient: Pick<
    PatientDto,
    | "createdAt"
    | "firstName"
    | "lastName"
    | "phoneNumber"
    | "deliveryAddress"
    | "dob"
    | "fullName"
    | "fromPlatformEnv"
  >,
  utm?: { utm_source: string; utm_medium: string; utm_campaign: string }
) => {
  const traits = {
    email: user.email,
    createdAt: patient?.createdAt || "",
    firstName: patient?.firstName || "",
    lastName: patient?.lastName || "",
    name: patient?.fullName || "",
    phone: patient?.phoneNumber || "",
    platform: patient?.fromPlatformEnv || "",
    region: CURRENT_COUNTRY,
    address: patient?.deliveryAddress
      ? pick(patient?.deliveryAddress, [
          "country",
          "state",
          "postal",
          "phoneNumber",
          "unit",
          "city",
          "region",
          "address"
        ])
      : null,
    birthday: patient?.dob ? new Date(patient?.dob) : null,
    id: user.id,
    ...(utm || getUtm())
  };

  cleanObject(traits);
  if (traits.address) {
    cleanObject(traits.address);
    traits.address.region =
      traits.address.region || (process.env.NEXT_PUBLIC_COUNTRY as LocalRegionType);
  }

  try {
    traitSchema.validateSync(traits, {
      abortEarly: false
    });
  } catch (error) {
    console.error(error);
    const msg = `Invalid traits: ${JSON.stringify(traits)}`;
    throw new Error(msg);
  }

  return traits;
};

export const getUtm = (): Record<string, string> => {
  const utmVars = ["utm_source", "utm_medium", "utm_campaign", "utm_id"];
  return utmVars.reduce((acc, utmVar) => {
    return {
      ...acc,
      [camelCase(utmVar)]:
        getTrackingFromQueryString(utmVar) ||
        getTrackingFromCookie(utmVar) ||
        getTrackingFromLocalStorage(utmVar)
    };
  }, {});
};

export const clearUtm = () => {
  const utmVars = ["utm_source", "utm_medium", "utm_campaign", "utm_id"];
  utmVars.forEach((utmVar) => {
    try {
      // clear cookie
      Cookies.remove(snakeCase(utmVar));
      // clear localStorage
      localStorage.removeItem(snakeCase(utmVar));
    } catch {
      console.error("Clearing utm issue");
    }
  });
};

export const getTracking = () => {
  return {
    referrer: document.referrer,
    userAgent: navigator.userAgent,
    sourceTracking: getTrackingByKey("source_tracking"),
    fbc: getTrackingByKey("fbc") || getTrackingByKey("_fbc"),
    fbp: getTrackingByKey("fbp") || getTrackingByKey("_fbp"),
    gclid: getTrackingByKey("gclid"),
    host: window.location.host,
    path: window.location.pathname
  };
};

export const setUtm = () => {
  const utm = getUtm();
  Object.keys(utm).forEach((key) => {
    if (utm[key]) {
      Cookies.set(snakeCase(key), utm[key], { expires: 30 });
      // localStorage.setItem(snakeCase(key), utm[key]);
    }
  });
};

export const setTracking = () => {
  const tracking = getTracking();
  Object.keys(tracking).forEach((key) => {
    if (tracking[key]) {
      localStorage.setItem(snakeCase(key), tracking[key]);
    }
  });
};

const getTrackingByKey = (key: string) => {
  return (
    getTrackingFromQueryString(key) ||
    getTrackingFromCookie(key) ||
    getTrackingFromLocalStorage(key) ||
    null
  );
};

export const getTrackingFromQueryString = (key: string) => {
  try {
    return window?.location?.search?.split(`${key}=`)[1]?.split("&")?.[0] || null;
  } catch {
    console.error("Tracking from query string issue");
    return null;
  }
};

export const getBrowserPlatform = (): string => {
  const userAgent = navigator.userAgent.toLowerCase();

  if (/mobile|android|iphone|ipad|ipod/i.test(userAgent)) {
    return "Mobile";
  }

  return "Web";
};

const getTrackingFromLocalStorage = (key: string) => {
  return localStorage.getItem(key);
};

const getTrackingFromCookie = (key: string) => {
  try {
    return document?.cookie?.split(`${key}=`)[1]?.split(";")?.[0] || null;
  } catch {
    console.error("Tracking from cookie issue");
    return null;
  }
};
